import React from "react";
import { useTranslation } from "react-i18next";
import { TransformedCheckoutData } from "../../../types/checkoutApiTypes";
import {
  TransformedAddress,
  TransformedCart,
} from "../../../../cart/types/cartApiTypes";

interface AddressSummaryProps {
  checkoutData: TransformedCheckoutData | undefined;
  cartData: TransformedCart | undefined;
  onEditBilling: () => void;
  onEditShipping: () => void;
}

const AddressSummary: React.FC<AddressSummaryProps> = ({
  checkoutData,
  cartData,
  onEditBilling,
  onEditShipping,
}) => {
  const { t } = useTranslation();

  const selectedShippingRate = cartData?.shippingRates?.find(
    (rate) => rate.selected,
  );

  const needsShipping = cartData?.needsShipping ?? true;

  const allCountryStates = window.checkoutFlowVariables?.countryStates || {};
  const allowedCountries = window.checkoutFlowVariables?.allowedCountries || {};

  const renderAddress = (
    address: TransformedAddress | undefined,
    type: "billing" | "shipping",
  ) => {
    if (!address) return null;

    const isCorporate = address.customerType === "corporate";
    const isIndividual = !isCorporate;
    const showCompany = type === "billing" && isCorporate;

    const statesForCountry = allCountryStates[address.country] || {};
    const stateName = address.state
      ? statesForCountry[address.state] || address.state
      : "";

    const countryName = allowedCountries[address.country] || address.country;

    return (
      <>
        <strong>
          {address.firstName} {address.lastName}
        </strong>
        <br />
        {showCompany && address.company && (
          <>
            {address.company}
            <br />
          </>
        )}
        {type === "billing" && address.country === "TR" && (
          <>
            {isIndividual && address.tckn && (
              <>
                {t("checkoutForm.fields.tckn", "TCKN")}: {address.tckn}
                <br />
              </>
            )}
            {isCorporate && address.vkn && (
              <>
                {t("checkoutForm.fields.vkn", "VKN")}: {address.vkn}
                <br />
              </>
            )}
          </>
        )}
        {address.address1}
        <br />
        {address.address2 && (
          <>
            {address.address2}
            <br />
          </>
        )}
        {address.postcode} {address.city} / {stateName}
        <br />
        {countryName}
        <br />
        {address.phone && (
          <>
            {address.phone}
            <br />
          </>
        )}
        {type === "billing" && address.email && (
          <>
            {address.email}
            <br />
          </>
        )}
      </>
    );
  };

  return (
    <div
      className={`order-review__grid ${
        !needsShipping ? "order-review__grid--single" : ""
      }`}
    >
      <div className="order-review__card">
        <div className="order-review__card-header">
          <h3 className="order-review__card-title">
            {t("orderReview.billingAddress", "Billing Address")}
          </h3>
          <button
            type="button"
            className="order-review__edit-btn"
            onClick={onEditBilling}
          >
            {t("orderReview.editButton", "Edit")}
          </button>
        </div>
        <div className="order-review__card-body">
          <address>
            {renderAddress(checkoutData?.billingAddress, "billing")}
          </address>
        </div>
      </div>

      {/* Shipping Address Card (Only show if needed) */}
      {needsShipping && (
        <div className="order-review__card">
          <div className="order-review__card-header">
            <h3 className="order-review__card-title">
              {t("orderReview.shippingAddress", "Shipping Address")}
            </h3>
            <button
              type="button"
              className="order-review__edit-btn"
              onClick={onEditShipping}
            >
              {t("orderReview.editButton", "Edit")}
            </button>
          </div>
          <div className="order-review__card-body">
            <address>
              {renderAddress(checkoutData?.shippingAddress, "shipping")}
            </address>

            {/* --- Selected Shipping Method Section --- */}
            {selectedShippingRate && (
              <div className="order-review__shipping-method">
                <h4 className="order-review__sub-title">
                  {t("orderReview.shippingMethod", "Shipping Method")}
                </h4>
                <div className="order-review__method-details">
                  <span className="order-review__method-name">
                    {selectedShippingRate.name}
                  </span>
                  <span className="order-review__method-price">
                    {selectedShippingRate.price}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressSummary;
